import clsx from "clsx";
import { useId, useState } from "react";
import { FieldHint } from "@/components/FieldHint";
import { useLocale } from "@/i18n";
import {
  formatLicensePlateOnBlur,
  getLicensePlateFormatHint,
  sanitizeLicensePlateInput,
} from "@/lib/licensePlate";

type Props = {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  missing?: boolean;
  className?: string;
  id?: string;
  hintText?: string | null;
};

export function LicensePlateInput({
  value,
  onChange,
  required,
  missing = false,
  className = "bk-input mt-1 uppercase",
  id: idProp,
  hintText,
}: Props) {
  const { messages: m } = useLocale();
  const autoId = useId();
  const inputId = idProp ?? autoId;
  const hintId = `${inputId}-hint`;
  const [touched, setTouched] = useState(false);

  const hint = touched ? getLicensePlateFormatHint(value) : null;

  return (
    <>
      <input
        id={inputId}
        type="text"
        className={clsx(className, missing && "bk-input--missing")}
        value={value}
        onChange={(e) => onChange(sanitizeLicensePlateInput(e.target.value))}
        onBlur={() => {
          setTouched(true);
          const formatted = formatLicensePlateOnBlur(value);
          if (formatted !== value) onChange(formatted);
        }}
        placeholder="z. B. AB-C 1234"
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        required={required}
        aria-invalid={hint || missing ? true : undefined}
        aria-describedby={hint ? hintId : undefined}
      />
      <p className="bk-field-hint">{hintText?.trim() || m.flow.licensePlateFormatHint}</p>
      {hint ? <FieldHint id={hintId}>{hint}</FieldHint> : null}
    </>
  );
}
